import { datadogLogs } from '@datadog/browser-logs'
import config from './runtime'
import { v4 as uuidv4 } from 'uuid'
import { sha256 } from 'js-sha256'
import { getUserData } from '../Utils/ServerUtil.js'

type LogAttributes = Record<string, any>

const sanitize = (attributes: LogAttributes = {}) => {
  const a = { ...attributes }
  ;[
    'authorization',
    'Authorization',
    'jwt',
    'token',
    'idToken',
    'user_hash',
    'email',
    'user_email',
  ].forEach((k) => {
    if ((a as any)[k] !== undefined) (a as any)[k] = '[redacted]'
  })
  return a
}

const setBaseContext = (ctx: LogAttributes = {}) => {
  datadogLogs.setGlobalContext({
    service: 'field-tool-web',
    env: 'prod',
    version: (config as any).BUILD_VERSION,
    ...ctx,
  })
}

class Logger {
  private static instance: Logger | null = null
  public currentUser?: any

  private constructor() {
    datadogLogs.init({
      clientToken: 'pubd62169a1180d6ca64ef53a1b72cac572',
      site: 'datadoghq.com',
      service: 'field-tool-web',
      env: 'prod',
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ['error', 'warn', 'info'],
      sampleRate: 100,
    })
    setBaseContext()
    this.applyUserContext()
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger()
    }
    return Logger.instance
  }

  private applyUserContext() {
    try {
      const user = getUserData && getUserData()
      if (user && (user.email || user.uuid)) {
        this.currentUser = user
        const orgId =
          Array.isArray(user.organizations) && user.organizations.length > 0
            ? user.organizations[0]?.organization_id
            : undefined
        datadogLogs.setUser({
          id: user.uuid || user.email,
          name: user.name,
          email: user.email,
        })
        if (orgId) {
          datadogLogs.setGlobalContextProperty('orgId', orgId)
        }
      }
    } catch (_e) {
      // Swallow if user context not yet available
    }
  }

  private log(level: 'debug' | 'info' | 'warn' | 'error', message: string, attributes?: LogAttributes) {
    this.ensureUserContext()
    datadogLogs.logger.log(message, sanitize(attributes), level)
  }

  debug(message: string, attributes?: LogAttributes) {
    this.log('debug', message, attributes)
  }

  private ensureUserContext() {
    if (!this.currentUser) {
      this.applyUserContext()
    }
  }
  info(message: string, attributes?: LogAttributes) {
    this.log('info', message, attributes)
  }
  warn(message: string, attributes?: LogAttributes) {
    this.log('warn', message, attributes)
  }
  error(message: string, attributes?: LogAttributes) {
    this.log('error', message, attributes)
  }

  setUser({ id, email, orgId }: { id?: string; email?: string; orgId?: string }) {
    this.currentUser = { id, email, orgId }
    datadogLogs.setUser({
      id: id || email,
      email,
    })
    if (orgId) {
      datadogLogs.setGlobalContextProperty('orgId', orgId)
    }
  }

  setRoute(path: string) {
    datadogLogs.setGlobalContextProperty('route', path)
  }

  setContext(ctx: LogAttributes) {
    setBaseContext(ctx)
  }
}

export const logger = Logger.getInstance()
export const initializeLogs = () => Logger.getInstance()
export const newRequestId = () => uuidv4()
export const requestStart = (requestId: string, attributes: LogAttributes = {}) => {
  const method = (attributes.method || '').toString().toUpperCase()
  const baseUrl = attributes.base_url as string | undefined
  const endpoint = attributes.endpoint as string | undefined
  const url = baseUrl ? `${baseUrl}${endpoint || ''}` : (endpoint || '')
  const context = {
    request_id: requestId,
    http: { method, url },
    response_type: attributes.response_type,
    orgId: attributes.orgId,
  }
  logger.info('request_start', context)
}
export const requestSuccess = (requestId: string, attributes: LogAttributes = {}) => {
  const method = (attributes.method || '').toString().toUpperCase()
  const baseUrl = attributes.base_url as string | undefined
  const endpoint = attributes.endpoint as string | undefined
  const url = baseUrl ? `${baseUrl}${endpoint || ''}` : (endpoint || '')
  const statusCode = attributes.status as number | undefined
  const context = {
    request_id: requestId,
    http: { method, url, status_code: statusCode },
    latency_ms: attributes.latency_ms,
  }
  logger.info('request_success', context)
}
export const requestFailure = (requestId: string, attributes: LogAttributes = {}) => {
  const method = (attributes.method || '').toString().toUpperCase()
  const baseUrl = attributes.base_url as string | undefined
  const endpoint = attributes.endpoint as string | undefined
  const url = baseUrl ? `${baseUrl}${endpoint || ''}` : (endpoint || '')
  const statusCode = attributes.status as number | undefined
  const context = {
    request_id: requestId,
    http: { method, url, status_code: statusCode },
    error_class: attributes.error_class,
    message_code: attributes.message_code,
  }
  logger.error('request_failure', context)
}
